/*******************************************************************************
  RobotCurrentMode.js

  Author: Advait

*******************************************************************************/

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectSite } from "../store/siteSlice";
import { PollSiteStatus } from "../apihelpers/poller";
import { siteDoAction } from "../apihelpers/apihelpers";
import "../index.css";

var POLL_PERIOD = 20000;

function RobotCurrentMode({ scheduleType }) {
  const [selectedSiteStatus, setSelectedSiteStatus] = useState({});
  const [modeChangeInProgress, setModeChangeInProgress] = useState(false);
  const [savedPoller, setPoller] = useState(null);
  const selectedSite = useSelector(selectSite);

  useEffect(() => {
    let statusPoller;
    if (selectedSite && selectedSite.id) {
      statusPoller = new PollSiteStatus({ siteStatusCb: siteStatusCb });
      statusPoller.startPoll(0, POLL_PERIOD, selectedSite.id);
      setPoller(statusPoller);
    }
    return () => {
      if (statusPoller) statusPoller.stopPoll();
    };
  }, [selectedSite]);

  //***************************************************************************
  // Callback from the status poller.
  // siteResponse - object response from the GET /site/<id> API
  //***************************************************************************
  function siteStatusCb(siteResponse) {
    setSelectedSiteStatus(siteResponse);
    // after a mode change, when we get next status we clear the mode change flag
    setModeChangeInProgress(false);
  }

  //***************************************************************************
  // handle button clicks to start/stop reception or inspection mode
  //***************************************************************************
  async function handleStartStopMode(event) {
    if (modeChangeInProgress) {
      return; // don't start another mode change if we just started one
    }
    if (!selectedSite || !selectedSite.id) {
      alert("Please select a site to start reception mode");
    } else {
      let action = "goto_idle"; // default, for the stop buttons
      if (event.target.id === "startReception") {
        action = "goto_reception";
      } else if (event.target.id === "startInspection") {
        action = "goto_inspection";
      } else if (event.target.id === "startPatrol") {
        action = "goto_security_patrol";
      }
      siteDoAction(selectedSite.id, action)
        .then((response) => {
          console.log("Mode change submitted successfully");
        })
        .catch((error) => {
          alert("Error in changing mode: ", error);
        });
      setModeChangeInProgress(true);
      //this.setState({modeChangeInProgress:true});
      // Restart poller, giving a few seconds for the robot to change modes
      // before we ask for what the new current mode is.
      savedPoller.startPoll(3000, POLL_PERIOD, selectedSite.id);
    }
  }

  //***************************************************************************
  // Get the current site mode, returning a string suitable for UI display.
  //***************************************************************************
  function getSiteMode(getSiteResponse) {
    let display_str = "disconnected";
    let mode_str = "unknown";
    try {
      if (getSiteResponse.robotid) {
        let connected =
          getSiteResponse.robotinfo &&
          getSiteResponse.robotinfo.status &&
          getSiteResponse.robotinfo.status.connected;
        let mode_reported = getSiteResponse.shadowstate && getSiteResponse.shadowstate.mode;
        let mode_desired =
          getSiteResponse.shadowstate_desired && getSiteResponse.shadowstate_desired.mode;
        mode_str = mode_reported;

        // report maintenance mode only if facilities status is IDLE and robot is in maintenance
        if (
          mode_reported === "IDLE" &&
          getSiteResponse.robotinfo?.configuration?.enabled === false
        ) {
          mode_str = "MAINTENANCE";
        }

        // Include the desired mode transition if the desired mode is different from reported mode
        if (mode_desired && mode_desired !== mode_reported) {
          mode_str += " -> " + mode_desired;
        }
        if (connected) {
          display_str = mode_str;
        } else {
          // If the robot is disconnected, there can still be useful mode information
          display_str = `disconnected (${mode_str})`;
        }
      }
    } catch (e) {
      // ignore errors, just use default
    }
    return display_str;
  }

  //***************************************************************************
  // start/stop buttons are enabled only if status says the site has a valid robot,
  // and based on robot health, and other site status.
  //***************************************************************************
  function isButtonEnabled(buttonid) {
    let enabled = false;
    try {
      let robotHealthy =
        selectedSiteStatus.robotinfo.health &&
        selectedSiteStatus.robotinfo.health.overallHealth &&
        (selectedSiteStatus.robotinfo.health.overallHealth === "OK" ||
          selectedSiteStatus.robotinfo.health.overallHealth === "OK_WARN") &&
        selectedSiteStatus.robotinfo.configuration?.enabled && // not maintenance mode
        selectedSiteStatus.robotinfo.status?.inLocalization && // robot is localized
        selectedSiteStatus.telemetry &&
        selectedSiteStatus.telemetry.thingExists &&
        selectedSiteStatus.telemetry.connected;

      if (
        selectedSite &&
        !modeChangeInProgress && // keep disabled for a few seconds after click
        selectedSiteStatus.robotid &&
        selectedSiteStatus.robotinfo.status.connected
      ) {
        let facilitiesState = selectedSiteStatus.facilitiesState;
        if (
          buttonid === "startReception" &&
          selectedSiteStatus.facilitiesState !== "RECEPTIONIST" &&
          robotHealthy
        ) {
          // Reception has priority, so allow start reception even if the site is in some other mode
          enabled = true;
        } else if (buttonid === "stopReception" && facilitiesState === "RECEPTIONIST") {
          enabled = true;
        } else if (
          buttonid === "startInspection" &&
          facilitiesState === "SECURITY_PATROL" &&
          robotHealthy
        ) {
          enabled = true;
        } else if (
          buttonid.startsWith("start") &&
          facilitiesState === "IDLE" &&
          robotHealthy
        ) {
          enabled = true;
        } else if (buttonid === "stopInspection" && facilitiesState === "INSPECTION") {
          enabled = true;
        } else if (buttonid === "stopPatrol" && facilitiesState === "SECURITY_PATROL") {
          enabled = true;
        }
      }
    } catch (e) {
      // ignore error, just keep button disabled
    }
    return enabled;
  }

  //***************************************************************************
  // render the UI
  //***************************************************************************
  let modeName = "unknown";
  if (scheduleType === "reception") {
    modeName = "Reception";
  } else if (scheduleType === "inspection") {
    modeName = "Inspection";
  } else if (scheduleType === "security_patrol") {
    modeName = "Patrol";
  }
  let idStart = "start" + modeName;
  let idStop = "stop" + modeName;

  const restrict = selectedSite.restrict_ui_page;
  if (
    (scheduleType === "reception" && restrict.reception) ||
    (scheduleType === "inspection" && restrict.inspection) ||
    (scheduleType === "security_patrol" && restrict.security_patrol)
  ) {
    return (
      <div className="individualContainer">
        <table className="individualContainerTable">
          <tbody>
            <tr>
              <td valign="top" style={{ paddingLeft: "15px" }}>
                <h4 style={{ width: "auto" }}>
                  Selected Site Does Not Support {modeName} Mode
                </h4>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  } else {
    return (
      <div className="individualContainer">
        <table className="individualContainerTable">
          <tbody>
            <tr>
              <td className="individualContainerTitleColumn" valign="top">
                <h4 className="individualContainerTitleHeading">Start and End {modeName}</h4>
              </td>
              <td>
                <table>
                  <tbody>
                    <tr>
                      <td>
                        <h6 style={{ paddingTop: "4px", width: "450px" }}>
                          Current Site Mode: {getSiteMode(selectedSiteStatus)}
                        </h6>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <div style={{ paddingTop: "10px" }}>
                          <button
                            id={idStart}
                            className="consoleButton"
                            onClick={handleStartStopMode}
                            disabled={!isButtonEnabled(idStart)}
                          >
                            Start {modeName} Now
                          </button>
                          <button
                            id={idStop}
                            className="consoleButton"
                            onClick={handleStartStopMode}
                            disabled={!isButtonEnabled(idStop)}
                          >
                            End {modeName}
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }
}
export default RobotCurrentMode;
